import {
  Component,
  getAssetPath,
  h,
  Host,
  Prop,
  State,
  Watch,
  ComponentInterface,
  Event,
  EventEmitter,
  Listen,
} from '@stencil/core';
import getEnv, { IEnv } from '../../utils/env';
import injectComponentScript from '../../utils/injectComponentScript';
import { BsWebcomponent } from './bsWebcomponent';
import { BaHeader } from '@design/bapf-pattern-library/source/js/_header';
import { Language } from '../../models/Language';
import { OiamAuthService } from './service/oiam-auth.service';
import { JWTReaderService } from './service/jwt-reader.service';
import { emitLanguageEvent, I18nUtil } from '../../utils/i18n/I18nUtil';
import { BahfEvents } from './types/bahf-event.types';
import { ActingType, OiamEvents } from '@oiambk/oiam-oauth-types';
import '@oiambk/oiam-session-timer-wc';
import '@oiambk/oiam-profile-header-wc';
import globalStore from '../../stores/globalStore';
import { fetchAndStore } from '../../utils/fetchAndStore';
import { LogoutBtn } from './types/logout-btn';
import { HeadLinkService } from './service/head-link.service';
import { IHfconfig } from '../../models/IHfconfig';
import { DefaultLanguages } from '../../models/DefaultLanguages';

@Component({
  tag: 'bahf-header',
  styleUrl: 'bahf-header.scss',
  assetsDirs: ['assets'],
  shadow: true,
})
export class BahfHeader implements ComponentInterface {
  private env: IEnv = getEnv();

  /**
   * Wird diesem Attribut der Wert true zugewiesen integriert die Header-Komponente automatisch den Cookie-Disclaimer
   * (DPL3). _true_ blendet auch den Button-Link "_Cookie-Einstellungen_" im Footer ein.
   */
  @Prop() withCookieDisclaimer: boolean = false;

  /**
   * Wird dieses optionale Attribut auf _true_ gesetzt, wird bei einem Klick auf Anmelden auf das Profil weitergeleitet,
   * ohne ein _oiamLoginEvent_ auszulösen. Wenn der Wert auf _false_ steht, wird beim Klick auf Anmelden der Login über
   * die OIAM-OAUTH-WC abgewickelt (durch Auslösen des Events _oiamLoginEvent_).
   */
  @Prop() loginLinkToProfile?: boolean = false;

  /**
   * Ein stringified Object mit einem Feld: languages.<br/>
   * <br/>
   * `languages: Language[]`<br/>
   * <br/>
   * `Language {`<br/>
   * `  code: string; // zweistellig`<br/>
   * `  href?: string; // optional`<br/>
   * `}`<br/>
   * <br/>
   * Bsp: `hfconfig='{"languages": [ {"code": "it","href": "https://www.arbeitsagentur.de/it"}, { weitere_Sprache... } ]}'`
   */
  @Prop() hfconfig?: string;

  /**
   * Wird dieses optionale Attribut auf _true_ gesetzt, werden die Komponenten MISO-Glocke, Vertrauensniveau, Postfach und Anmelden-Button ausgeblendet.
   * Es sind nur das Logo, die Suche, das Burger-Menü (_Megaflyout_) und der Abmelden-Button sichtbar.
   */
  @Prop() reduced?: boolean = false;

  /**
   * Konfiguriert das Verhalten des Abmelden-Buttons
   * OIAM: Abmelden-Button wird angezeigt abhängig vom OIAM-Token-Event (default).
   * SHOW: Abmelden-Button wird immer angezeigt unabhängig vom OIAM-Token-Event.
   * HIDE: Anmelden- und Abmelden-Buttons werden nicht angezeigt.
   *
   * ACHTUNG: Der Abmelden-Button sollte nur in seltenen Fällen ausgeblendet werden!
   */
  @Prop({reflect: true})
  dangerouslySetLogoutBtn: LogoutBtn = LogoutBtn.OIAM;

  /**
   * Wird dieses optionale Attribut auf _true_ gesetzt, wird die vom Header ermittelte Sprache (detectedLanguage)
   * anstelle der aktiven Header-Sprache (activeLanguage) als Cookie verwendet.
   */
  @Prop() useDetectedLanguage?: boolean = false;

  /**
   * Wird dieses optionale Attribut auf _true_ gesetzt, wird die Sprache aus dem HTML-lang-Attribut
   * vor dem bahf_lang-Cookie ausgelesen.
   */
  @Prop() prioritizeLangAttr?: boolean = false;

  @Watch('hfconfig')
  async headerCustomConfigWatcher(param: string) {
    this.parsedHfconfig = this.parseHfconfig(param);
    //add labels to languages
    this.hfConfigLanguages = this.addLabelToLanguages(this.parsedHfconfig?.languages || []);
  }

  @State() private jsonData: any;

  @State() loggedIn = false;
  @State() actingType: ActingType = 'privatperson';
  @State() private languages: {
    activeLanguage: Language;
    detectedLanguage: Language;
  };
  @State() private isLoginButtonVisible: boolean = false;

  @State() private oiamTokenEventReceived: boolean = false;

  @State() private alternateLinkLanguages: Language[] = HeadLinkService.readLinksFromHead();

  @State() private parsedHfconfig: IHfconfig | null = this.parseHfconfig(this.hfconfig);

  @State() private hfConfigLanguages: Language[] = this.addLabelToLanguages(this.parsedHfconfig?.languages || []);

  /**
   * Dieses Event wird getriggert, wenn <bahf-header> an den DOM connectet wird.<br/>
   * <br/>
   * Die Payload enthält ein Objekt mit zwei Feldern:<br/>
   * <br/>
   * - activeLanguage: ein Objekt mit den Feldern code (String) und label (String).
   * activeLanguage ist die Sprache, welche der Header für sich selbst verwendet und auch für
   * das Cookie "bahf_lang" setzt. Es kann nur "de" | "en" | "es" annehmen.<br/>
   * <br/>
   * - detectedLanguage: ein Objekt mit den Feldern code (String) und label (String).
   * detectedLanguage ist die Sprache, welche der Header für die Anwendung ermittelt hat. Die
   * Sprache wird ermittelt aus URL-Parameter, Query-Parameter, Cookie oder HTML-Lang-Attribut
   * (in dieser Reihenfolge).
   */
  @Event({ eventName: 'bahfLanguage' }) bahfLanguageEmitter: EventEmitter<{
    activeLanguage: Language;
    detectedLanguage: Language;
  }>;

  @Listen('bahfUpdateLanguages', {target: 'window'})
  async updateLanguages() {
    await this.initializeHeaderLanguagesAndData();
  }

  @Listen('bahfSendLanguage', {target: 'document'})
  sendLanguage() {
    if (!this.languages) {
      this.languages = I18nUtil.setLanguages(this.hfConfigLanguages, this.alternateLinkLanguages, this.useDetectedLanguage, this.prioritizeLangAttr);
    }
    emitLanguageEvent(this.bahfLanguageEmitter, this.languages)
  }

  headerElem: HTMLHeadElement;

  componentDidLoad() {
    this.initHeader();
    this.removeFallbackHeader();
    this.headerElem && this.headerElem.classList.remove('initializing');

    window.self.addEventListener(BahfEvents.LOGINCLICKED, () => {
      if (this.loginLinkToProfile) {
        //redirect to profile
        window.location.href = this.env.profilEndpoint;
      } else {
        //send login event to oiam wc
        window.self.dispatchEvent(new CustomEvent(OiamEvents.DOLOGIN));
      }

      //warning if nothing happens
      setTimeout(() => {
        console.warn('Login Redirect not working.');
      }, 3000);
    });

    window.self.addEventListener(OiamEvents.ERROR, (e: CustomEvent) =>
      OiamAuthService.handleError(e)
    );

    window.self.addEventListener(OiamEvents.IDLESESSIONEXPIRATION, () => {
      console.log('session expired');
      this.loggedIn = false;
    });

    this.initFallbackTimeouts();

    window.self.dispatchEvent(new CustomEvent(BahfEvents.LOADED));
  }

  componentWillLoad() {
    window.self.addEventListener(OiamEvents.READYFORLOGIN, () => {
      this.isLoginButtonVisible = true;
    });

    window.self.addEventListener(OiamEvents.TOKEN, (e: CustomEvent) => {
      this.oiamTokenEventReceived = true;
      if (e.detail && e.detail.jwt) {
        this.actingType = JWTReaderService.getActingTypeFromJwt(e.detail.jwt);
        this.loggedIn = JWTReaderService.isValidJwt(e.detail.jwt);
      } else {
        //JWT is empty -> user is not logged in
        this.loggedIn = false;
      }

      if (e.detail?.clientid) {
        setTimeout(() => {
          this.isLoginButtonVisible = true;
        }, 10);
      }
    });

    this.renderCookieDisclaimer();
  }

  private initFallbackTimeouts() {
    setTimeout(() => {
      //embedded web components like HF may miss the TOKEN-Event
      //Ask for Token after 1s if oiamTokenEvent was not received
      if (!this.oiamTokenEventReceived && !this.loggedIn) {
        OiamAuthService.askForToken();
      }
    }, 500);
  }

  private initHeader() {
    if (this.headerElem) {
      const _baHeader = new BaHeader(this.headerElem);
      const _bsWebcomponent = new BsWebcomponent(this.headerElem);

      document.addEventListener('keyup', () => {
        const isActiveElementInFlyoutContainer = this.isActiveElementInFlyoutContainer();
        if (isActiveElementInFlyoutContainer) {
          this.headerElem.classList.remove('ba-keyboard-mode');
        } else {
          this.headerElem.classList.add('ba-keyboard-mode');
        }
      });
      document.addEventListener('mousedown', () => {
        this.headerElem.classList.remove('ba-keyboard-mode');
      });
    }
  }

  private getActiveElement(): Element {
    let activeElement: Element;
    const customTags = ['miso-glocke', 'authn-level-shield', 'session-timer', 'profile-menu'];

    for (const element of customTags) {
      const shadowRoot: null | ShadowRoot = this.headerElem.querySelector(element)?.shadowRoot;
      activeElement = shadowRoot?.activeElement;
      if (!!activeElement) break;
    }

    return activeElement ?? document.activeElement;
  }

  private isActiveElementInFlyoutContainer(): boolean {
    return Boolean(this.getActiveElement()?.closest('div.ba-flyout-container'));
  }

  private renderCookieDisclaimer() {
    if (this.withCookieDisclaimer) {
      const disclaimerElem = document.createElement('bahf-cookie-disclaimer-dpl3');
      // @ts-ignore
      document.body.insertBefore(disclaimerElem, document.body.childNodes[0]);
    }
  }

  private removeFallbackHeader() {
    const fallback = document.querySelector('bahf-fallback-header');
    if (fallback) {
      fallback.parentNode.removeChild(fallback);
    }
  }

  private parseHfconfig(hfconfig: string): IHfconfig | null {
    if (!hfconfig) return null;

    try {
      return JSON.parse(hfconfig);
    } catch {
      console.warn('Ungültiges Format von Property "hfconfig".');
      return null;
    }
  }

  async componentWillRender() {
    if (!this.languages) {
      this.initializeHeaderLanguagesAndData();
    }

    if (this.loggedIn && !this.isInAppBrowser()) {
      // Laden der webcomponent Scripte für MISO-Glocke
      injectComponentScript(this.env.glockenEndpoint + 'miso-glocke.esm.js');
      injectComponentScript(this.env.glockenEndpoint + 'miso-glocke.js');
    }
  }

  private async initializeHeaderLanguagesAndData(): Promise<void> {
    this.languages = I18nUtil.setLanguages(this.hfConfigLanguages, this.alternateLinkLanguages, this.useDetectedLanguage, this.prioritizeLangAttr);
    emitLanguageEvent(this.bahfLanguageEmitter, this.languages);

    setTimeout(() => {
      emitLanguageEvent(this.bahfLanguageEmitter, this.languages);
    }, 500);

    globalStore.state.language = this.languages.activeLanguage.code;
    this.jsonData = await fetchAndStore(`assets/header.${this.languages.activeLanguage.code.toLowerCase()}.json`);
  }

  private addLabelToLanguages(languages: Omit<Language, 'label'>[]): Language[] {
    return languages?.map(lang => ({...lang, label: I18nUtil.mapLangCodeToLocalName(lang.code) })) || []
  }

  render() {
    return (
      <Host>
        {this.renderHeader()}
        <bahf-maintenance-info/>
        <session-expiration-30m-warn-popup/>
        <session-expiration-5m-warn-popup/>
        <session-expiration-inactivity-warn-popup/>
      </Host>
    );
  }

  private isInAppBrowser() {
    return sessionStorage.getItem('bahf_inapp') === 'hide';
  }

  private renderHeader() {
    if (!this.isInAppBrowser()) {
      return (
        <header
          class={`initializing ba-header ba-header-element ${this.loggedIn ? 'ba-user-logged-in' : ''}`}
          ref={(el: HTMLHeadElement) => (this.headerElem = el)}
          lang={this.languages?.activeLanguage.code?.toLowerCase()}
        >
          <div class="container-fluid">
            <div class="ba-logo">
              {this.jsonData && <a href={this.jsonData?.logoTargetUrl} title={this.jsonData?.logoTitle}>
                <img
                  src={getAssetPath(this.jsonData?.logo)}
                  alt={this.jsonData?.logoAltText}
                />
              </a>}
            </div>
          </div>
          <nav class="ba-nav-meta" aria-label="Metanavigation">
            <bahf-language-switch-desktop
              currentLanguage={this.languages?.detectedLanguage}
              hfConfigLanguages={this.hfConfigLanguages}
              navMetaDropdownAriaLabel={this.jsonData?.navMetaDropdownAriaLabel}
              srSelectionLabel={this.jsonData?.megaflyout?.srSelectionLabel}
            />
          </nav>

          {this.renderHauptnavigation()}
        </header>
      );
    } else {
      return (
        <session-timer isInvisible={true}></session-timer>
      );
    }
  }

  private renderHauptnavigation() {
    const isLangDe: boolean = this.languages.activeLanguage.code === DefaultLanguages.de.code;

    return (
      <nav class="ba-nav-main" aria-labelledby="ba-hauptnavigation">
        <h1 id="ba-hauptnavigation" class="sr-only">
          {this.jsonData?.srNavLabel}
        </h1>
        <ul class="container-fluid">
          {isLangDe && <li>
            <a
              href={this.env.sucheEndpoint}
              title={this.jsonData?.searchButton}
              role="button"
              class="ba-menu-entry ba-search"
              data-testid="bahf-button-search"
            >
              <span class="ba-title">{this.jsonData?.searchButton}</span>
            </a>
          </li>}

          {/*Glocke*/}
          {!this.reduced && (
            <li>
              <miso-glocke></miso-glocke>
            </li>
          )}

          {/*Vertrauensniveau*/}
          {!this.reduced && (
            <li>
              <authn-level-shield></authn-level-shield>
            </li>
          )}

          {/*Sessionzeit*/}
          {!this.reduced && (
            <li>
              <session-timer></session-timer>
            </li>
          )}

          {/*Profilmenue JWT 2.9*/}
          {(!this.reduced
            && this.dangerouslySetLogoutBtn === LogoutBtn.OIAM) && (
            <li>
              <profile-menu></profile-menu>
            </li>
          )}
          {
            ((this.reduced && this.loggedIn)
              || this.dangerouslySetLogoutBtn === LogoutBtn.SHOW) &&
            <li>
              <a href="#"
                 data-testid="bahf-button-logout"
                 class="ba-menu-entry ba-logout"
                 role="button"
                 onClick={(event) => {
                   event.preventDefault();
                   OiamAuthService.sendLogout();
                 }}><span class="ba-title">{this.jsonData?.logoutButton}</span></a>
            </li>
          }

          {!this.loggedIn
            && this.isLoginButtonVisible
            && !this.reduced
            && this.dangerouslySetLogoutBtn === LogoutBtn.OIAM
            && (
              <li>
                <a
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    OiamAuthService.sendLogin();
                  }}
                  title={this.jsonData?.loginButton}
                  role="button"
                  class="ba-menu-entry ba-login"
                  data-testid="bahf-button-login"
                >
                  <span class="ba-title">{this.jsonData?.loginButton}</span>
                </a>
              </li>
            )}
          <li class="ba-header-megaflyout-container">
            <bahf-header-megaflyout
              hfConfigLanguages={this.hfConfigLanguages}
              languages={this.languages}
              jsonData={this.jsonData?.megaflyout}
              loggedIn={this.loggedIn}
              actingType={this.actingType}
            />
          </li>
        </ul>
      </nav>
    );
  }
}
